import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { eventsApi } from 'src/Services/API';
import { EventDetailsDtoOriginEnum, EventDto } from 'src/Services/API/generated';
import { useEventFiltersStore } from 'src/Stores/eventFiltersStore';

export interface LocalEvent extends EventDto {
  localId: string;
}

const useFetchEvents = ({ startDate, endDate }: { startDate: Dayjs; endDate: Dayjs }) => {
  const assetId = useCurrentAssetId();
  const { t } = useTranslation();

  // Connect to the Zustand store for event type filters
  const selectedEventTypes = useEventFiltersStore(state => state.selectedEventTypes);

  // Create the query key that includes filters
  const queryKey = [
    'fetch-events',
    assetId,
    startDate?.toISOString(),
    endDate?.toISOString(),
    selectedEventTypes,
  ];

  // Fetch events with API filtering
  const queryResult = useQuery({
    queryKey,
    queryFn: async () => {
      if (!assetId) throw new Error('No assetId provided');

      const events = await eventsApi.eventsControllerFindAll(
        startDate.toISOString(),
        endDate.toISOString(),
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        assetId,
        selectedEventTypes.length > 0 ? selectedEventTypes : undefined
      );

      return events?.data
        .map(event => ({
          ...event,
          localId: `${event.id}-${event.startTime}`,
        }))
        .map(event => ({
          ...event,
          name:
            event.origin === EventDetailsDtoOriginEnum.PracticalInformation
              ? t(`practicalInformation.subtypes.${event.description}`)
              : event.name,
          description:
            event.origin === EventDetailsDtoOriginEnum.PracticalInformation
              ? t(`practicalInformation.placeholders.${event.name}`)
              : event.description,
        }));
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    enabled: !!assetId && startDate !== undefined && endDate !== undefined,
    keepPreviousData: true,
  });

  return queryResult;
};

export default useFetchEvents;
